/**
 * Homebrew Storage Service
 * Firestore CRUD for user homebrew content (mechanics, ancestries, communities, etc.)
 *
 * Firestore path: users/{userId}/homebrewContent/{contentId}
 * Uses dynamic imports for Firebase to avoid bundling in static builds.
 */

/**
 * Convert a name string to lowercase kebab-case.
 * "My Cool Ability" -> "my-cool-ability"
 */
const slugify = (name) =>
  (name || 'untitled')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

/**
 * Generate a unique homebrew content ID.
 * Format: hb_{userId prefix}_{slugified name}_{random suffix}
 */
const generateId = (userId, name) => {
  const suffix = Math.random().toString(36).slice(2, 6);
  return `hb_${userId.slice(0, 8)}_${slugify(name)}_${suffix}`;
};

/**
 * Save new or update existing homebrew content.
 *
 * - If content.id exists and starts with "hb_", updates the existing doc.
 * - Otherwise, generates a new ID and creates a new doc.
 * - Always sets meta.updatedAt to server timestamp.
 * - Sets meta.createdAt and meta.authorId only on new docs.
 *
 * @param {string} userId - Firebase auth UID
 * @param {object} content - Homebrew content object { id?, type, status, meta, data, sharing }
 * @returns {Promise<string>} The saved document ID
 */
export const saveHomebrewContent = async (userId, content) => {
  const { collection, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('../firebase/config');

  const isUpdate = content.id && content.id.startsWith('hb_');
  const contentId = isUpdate ? content.id : generateId(userId, content.meta?.name);

  const colRef = collection(db, 'users', userId, 'homebrewContent');
  const docRef = doc(colRef, contentId);

  if (isUpdate) {
    // Use setDoc with merge so it works for both first-time saves (doc may not exist yet)
    // and subsequent updates (doc already exists)
    await setDoc(docRef, {
      type: content.type,
      status: content.status || 'draft',
      meta: {
        ...content.meta,
        updatedAt: serverTimestamp(),
      },
      data: content.data || {},
      sharing: content.sharing || { visibility: 'private' },
    }, { merge: true });
  } else {
    await setDoc(docRef, {
      type: content.type,
      status: content.status || 'draft',
      meta: {
        ...content.meta,
        authorId: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      data: content.data || {},
      sharing: content.sharing || { visibility: 'private' },
    });
  }

  return contentId;
};

/**
 * Load ALL homebrew content for a user.
 *
 * @param {string} userId - Firebase auth UID
 * @returns {Promise<Array>} Array of { id, type, status, meta, data, sharing }
 */
export const loadHomebrewContent = async (userId) => {
  if (!userId) return [];
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const snapshot = await getDocs(
      collection(db, 'users', userId, 'homebrewContent')
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Failed to load homebrew content:', error);
    return [];
  }
};

/**
 * Load homebrew content filtered by type.
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} type - Content type ('mechanic' | 'ancestry' | 'community' | 'domain-card' | 'campaign-frame')
 * @returns {Promise<Array>} Filtered array of homebrew content
 */
export const loadHomebrewContentByType = async (userId, type) => {
  if (!userId) return [];
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');

    const snapshot = await getDocs(
      query(
        collection(db, 'users', userId, 'homebrewContent'),
        where('type', '==', type)
      )
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error(`Failed to load homebrew content of type "${type}":`, error);
    return [];
  }
};

/**
 * Delete a homebrew content document.
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} contentId - Document ID to delete
 * @returns {Promise<void>}
 */
export const deleteHomebrewContent = async (userId, contentId) => {
  const { doc, deleteDoc } = await import('firebase/firestore');
  const { db } = await import('../firebase/config');

  await deleteDoc(doc(db, 'users', userId, 'homebrewContent', contentId));
};

/**
 * Duplicate a homebrew content document.
 * Creates a copy with "(Copy)" appended to the name, status set to 'draft',
 * and fresh timestamps.
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} contentId - Document ID to duplicate
 * @returns {Promise<string>} The new document ID
 */
export const duplicateHomebrew = async (userId, contentId) => {
  const { doc, getDoc } = await import('firebase/firestore');
  const { db } = await import('../firebase/config');

  const docRef = doc(db, 'users', userId, 'homebrewContent', contentId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    throw new Error(`Homebrew content "${contentId}" not found`);
  }

  const original = snapshot.data();
  const copyName = `${original.meta?.name || 'Untitled'} (Copy)`;

  return saveHomebrewContent(userId, {
    type: original.type,
    status: 'draft',
    meta: {
      ...original.meta,
      name: copyName,
      version: 1,
    },
    data: original.data || {},
    sharing: { visibility: 'private' },
  });
};

/**
 * Update the status of a homebrew content document (draft <-> ready).
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} contentId - Document ID
 * @param {string} status - New status ('draft' | 'ready')
 * @returns {Promise<void>}
 */
export const updateHomebrewStatus = async (userId, contentId, status) => {
  const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('../firebase/config');

  await updateDoc(doc(db, 'users', userId, 'homebrewContent', contentId), {
    status,
    'meta.updatedAt': serverTimestamp(),
  });
};

/**
 * Update the review status of a homebrew mechanic.
 * @param {string} userId
 * @param {string} contentId
 * @param {'approved'|'auto'|'needsReview'} reviewStatus
 */
export async function updateReviewStatus(userId, contentId, reviewStatus) {
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('../firebase/config');

  const docRef = doc(db, 'users', userId, 'homebrewContent', contentId);
  await setDoc(docRef, { reviewStatus, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Export homebrew content as a JSON file download (pure client-side).
 *
 * @param {object} content - The homebrew content object to export
 */
/**
 * Import a single composed mechanic from JSON.
 * Validates the mechanic, generates a hb_ ID, and saves to Firestore.
 *
 * Accepts either:
 *   - A raw composed mechanic JSON (has `id`, `version`, `meta`, `actions`)
 *   - A Storeroom export wrapper (has `type`, `data`, where `data` is the mechanic)
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} jsonString - JSON string to parse and import
 * @returns {Promise<{ success: boolean, contentId?: string, errors?: string[] }>}
 */
export const importHomebrew = async (userId, jsonString) => {
  const { validateComposedMechanic } = await import('../engine/schema');

  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return { success: false, errors: ['Invalid JSON: could not parse input'] };
  }

  // Unwrap Storeroom export format if needed
  const mechanic = parsed.data && parsed.type ? parsed.data : parsed;

  const { valid, errors } = validateComposedMechanic(mechanic);
  if (!valid) {
    return { success: false, errors };
  }

  // Determine content type from containerContext
  const containerContext = mechanic.meta?.containerContext;
  const type = containerContext === 'domain-card' ? 'mechanic' : 'mechanic';

  const contentId = await saveHomebrewContent(userId, {
    type,
    status: 'draft',
    meta: {
      name: mechanic.meta?.name || 'Imported Mechanic',
      summary: mechanic.meta?.summary || '',
      source: 'homebrew',
    },
    data: mechanic,
    sharing: { visibility: 'private' },
  });

  return { success: true, contentId };
};

/**
 * Batch import multiple composed mechanics from a JSON array.
 *
 * @param {string} userId - Firebase auth UID
 * @param {string} jsonString - JSON string of an array of mechanics
 * @returns {Promise<{ success: Array<string>, failed: Array<{ index: number, errors: string[] }> }>}
 */
export const importHomebrewBatch = async (userId, jsonString) => {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return { success: [], failed: [{ index: 0, errors: ['Invalid JSON: could not parse input'] }] };
  }

  if (!Array.isArray(parsed)) {
    return { success: [], failed: [{ index: 0, errors: ['Expected a JSON array for batch import'] }] };
  }

  const success = [];
  const failed = [];

  for (let i = 0; i < parsed.length; i++) {
    const result = await importHomebrew(userId, JSON.stringify(parsed[i]));
    if (result.success) {
      success.push(result.contentId);
    } else {
      failed.push({ index: i, errors: result.errors });
    }
  }

  return { success, failed };
};

export const exportHomebrew = (content) => {
  const filename = `${content.meta?.name || 'homebrew'}.json`;
  const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
